import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Rating
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { apiService } from '../../utils/api';

/**
 * ReviewSubmitForm - クチコミ投稿フォーム
 * 
 * Props:
 *   targetId: 企業/投稿ユーザーのUID
 *   targetType: "corporate" | "poster"
 *   onSubmitted: 投稿成功時のコールバック
 */
const ReviewSubmitForm = ({ targetId, targetType, onSubmitted }) => {
  const [text, setText] = useState('');
  const [rating, setRating] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const MAX_LENGTH = 2000;
  const MIN_LENGTH = 5;

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setText(value);
      setCharCount(value.length);
    }
  };

  const handleRatingChange = (e, value) => {
    setRating(value || 3);
  };

  const handleSubmit = async () => {
    setError('');

    // バリデーション
    if (text.length < MIN_LENGTH) {
      setError(`クチコミは${MIN_LENGTH}文字以上で入力してください`);
      return;
    }

    try {
      setLoading(true);
      await apiService.post('/api/reviews', {
        target_id: targetId,
        target_type: targetType,
        text: text.trim(),
        rating: rating
      });
      
      setText('');
      setRating(3);
      setCharCount(0);
      if (onSubmitted) {
        onSubmitted();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'クチコミの投稿に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const isValid = text.length >= MIN_LENGTH;

  return (
    <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          クチコミを投稿する
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          投稿されたクチコミは、相手の承認後に公開されます。承認までは投稿者本人にのみ「承認待ち」として表示されます。
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box>
          {/* 星評価入力 */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              評価:
            </Typography>
            <Rating
              value={rating}
              onChange={handleRatingChange}
              disabled={loading}
              size="medium"
            />
            <Typography variant="caption" color="text.secondary">
              {rating}/5
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="このお店の感想を教えてください..."
            value={text}
            onChange={handleTextChange}
            disabled={loading}
            sx={{ mb: 1 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {charCount} / {MAX_LENGTH} 文字
            </Typography>
            {charCount > 0 && charCount < MIN_LENGTH && (
              <Typography variant="caption" color="error">
                あと {MIN_LENGTH - charCount} 文字必要です
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={!isValid || loading}
            fullWidth
          >
            {loading ? '投稿中...' : 'クチコミを投稿'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReviewSubmitForm;
